<template>
<a-config-provider>
    <!-- 지원 탭 배너: 모든 router-view 분기 위(사이드 메뉴 없는 화면에도), 닫기 없음(계약 SUPPORT 5) -->
    <SupportBanner v-if="!isSupportEntryPage" />
    <img src="@/assets/img/login/finger-pointer.png" class="finger-pointer"  >
    <router-view v-if="shouldShowSideNav"/>
    <div v-if="!shouldShowSideNav" :style="shouldShowSideNav? 'margin-left: 0 !important; display: flex; height:100%;' : ' height:100%;'">
        <LoadingSpinner v-if="this.$store.state.isLoading" />
        <Sidenav v-if="!shouldShowSideNav" class="fixed-start" />
        <!-- 첫 로그인 「고객 지원을 위한 접근 허용」 꼭 고르기 창 — 로그인 뒤 껍데기가 뜰 때 한 번 확인(어느 화면으로 들어와도) -->
        <SupportConsentCard choice />
        <main
            v-if="!shouldShowSideNav"
            class="main-content position-relative"
            :style="shouldShowSideNav
            ? 'margin-left: 0 !important; display: flex;' : ''"
        >
            <router-view v-if="!shouldShowSideNav"/>
        </main>
        <div v-else>
            <router-view/>
        </div>
    </div>
    <OtpVerifyModal ref="stepUpModal" />
    <!-- 직원 권한 정기 확인(6개월): 로그인 뒤 껍데기에서 탭·계정마다 한 번, 로그아웃하면 표시를 지움(T-CONTRACT-1031 §2) -->
    <PermReviewModal :active="!shouldShowSideNav" />
</a-config-provider>
</template>
<script>
import { oneBtnDialog, } from '@/utils/utils';
import { bridges } from '@/utils/bridges';
import { RouterEnum } from '@/router/router.enums';
import Sidenav from '@/views/components/Sidenav';
import LoadingSpinner from '@/views/components/Loading.vue';
import BrowserNotificationManager from '@/utils/browserNotificationManager';
import OtpVerifyModal from '@/views/components/OtpVerifyModal.vue';
import { setStepUpHandler } from '@/utils/mfa';
import SupportBanner from '@/views/components/SupportBanner.vue';
import SupportConsentCard from '@/views/components/SupportConsentCard.vue';
import PermReviewModal from '@/views/components/PermReviewModal.vue';
import { isSupportTab } from '@/utils/tokenStore';
import { setPermReloadHandler } from '@/utils/standardErrors';
import { reloadPerms } from '@/services/permState';

// 지원 진입·끝·앱 사이 이동 받기 화면(사이드 메뉴·배너 없이 그림)
const SUPPORT_ENTRY_PATHS = [RouterEnum.SupportEnter.path, RouterEnum.SupportEnded.path, RouterEnum.HandoffEnter.path];

export default {
    name: 'App',
    components: {
        Sidenav,
        LoadingSpinner,
        OtpVerifyModal,
        SupportBanner,
        SupportConsentCard,
        PermReviewModal,
    },
    computed: {
        isSupportEntryPage() {
            return SUPPORT_ENTRY_PATHS.includes(this.path);
        },
        shouldShowSideNav() {
            const specialRoutes = [
                RouterEnum.MobilePages.path, RouterEnum.KakaoTalk.path,
                RouterEnum.Signin.path, RouterEnum.Signin.SetIdAndPw.path,
                RouterEnum.Signin.FindId.path, RouterEnum.Signin.FindIdDone.path,
                RouterEnum.Signin.ResetPwdAuth.path, RouterEnum.Signin.ResetPwd.path,
                RouterEnum.Signin.ResetPwdDone.path,
                RouterEnum.MobileCertificateList.path,
                RouterEnum.Signup.path,
                ...SUPPORT_ENTRY_PATHS,
            ];
            return specialRoutes.some(path => this.path.startsWith(path));
        },
    },
    watch: {
        '$route'(newRoute, oldRoute) {
            this.path = newRoute.path.toLowerCase();
            // 브라우저 알림 매니저에 라우터 변경 알림
            if (this.browserNotificationManager) {
                this.browserNotificationManager.handleRouteChange(newRoute, oldRoute);
            }
        },
    },
    data() {
        return {
            RouterEnum,
            navWidth: 'calc(100% - 220px)',
            navWidthOri: 'calc(100% + 220px)',
            isInternetConnected: true,
            path: this.$route.path,
            role: 'HOSPITAL',
            timer: null,
            interval: process.env.NODE_ENV === 'production' ? 3600000 : 60000,
            // 브라우저 알림 시스템 관리자
            browserNotificationManager: null,
            // 알림 매니저 초기화 상태
            notificationManagerInitialized: false
        };
    },
    created() {
        this.setLiveVersionObserver();
    },
    beforeUnmount() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        
        // 브라우저 알림 매니저 정리
        if (this.browserNotificationManager) {
            this.browserNotificationManager.destroy();
            this.browserNotificationManager = null;
        }
        
        window.removeEventListener('resize', this.handleScreenSizeRedirection);
    },
    async mounted() {
        this.handleScreenSizeRedirection();
        window.addEventListener('resize', this.handleScreenSizeRedirection);
        this.setOnlineObserver();
        this.initBridges();
        // MFA_REQUIRED·SEND_AUTH_REQUIRED 를 받은 요청이 여는 인증번호 창(axios.js)
        setStepUpHandler(purpose => this.$refs.stepUpModal.start({ mode: 'stepUp', purpose }));
        // 403 PERMISSION_DENIED 뒤 권한(introspect) 한 번 다시 읽기(axios.js — W6 E2)
        setPermReloadHandler(reloadPerms);
        
        // 브라우저 알림 시스템 초기화 (지연 실행)
        // this.$nextTick(() => {
        //     this.initializeNotificationManager();
        // });
    },

    methods: {
        /**
         * 브라우저 알림 매니저 초기화
         */
        async initializeNotificationManager() {

            // 지원 탭에서는 브라우저 알림(기기 push) 구독을 시작하지 않음 — 서버가 서브 계정의 기기 등록을 거절(W6 C3)
            if (isSupportTab()) return;
            // 중복 초기화 방지
            if (this.notificationManagerInitialized) {
                console.log('[App] 브라우저 알림 매니저가 이미 초기화됨');
                return;
            }

            try {
                // 필수 의존성 확인
                if (!this.$apiInstance) {
                    console.warn('[App] API 인스턴스가 준비되지 않음, 알림 매니저 초기화 지연');
                    setTimeout(() => this.initializeNotificationManager(), 1000);
                    return;
                }

                if (!this.$router) {
                    console.warn('[App] 라우터가 준비되지 않음, 알림 매니저 초기화 지연');
                    setTimeout(() => this.initializeNotificationManager(), 1000);
                    return;
                }

                // BrowserNotificationManager 인스턴스 생성
                this.browserNotificationManager = new BrowserNotificationManager(this.$apiInstance, this.$router);
                
                // 브라우저 알림 시스템 초기화 및 감지 시작
                await this.browserNotificationManager.init();
                
                this.notificationManagerInitialized = true;
                console.log('[App] 브라우저 알림 매니저 초기화 완료');
                
            } catch (error) {
                console.error('[App] 브라우저 알림 매니저 초기화 실패:', error);
                
                // 재시도 로직 (최대 3회)
                if (!this.notificationRetryCount) {
                    this.notificationRetryCount = 0;
                }
                
                this.notificationRetryCount++;
                if (this.notificationRetryCount < 3) {
                    console.log(`[App] 브라우저 알림 매니저 초기화 재시도 (${this.notificationRetryCount}/3)`);
                    setTimeout(() => this.initializeNotificationManager(), 5000);
                } else {
                    console.error('[App] 브라우저 알림 매니저 초기화 최종 실패');
                }
            }
        },

        initBridges() {
            const bridgesMethods = bridges();
            Object.entries(bridgesMethods).forEach(([key, value]) => {
                window[key] = value;
            });
        },
        async setLiveVersionObserver() {
            await this.getLiveVersion();

            if(!this.isInternetConnected) return;
            if(this.timer) return;

            this.timer = setInterval(async () => {
                if(!this.isInternetConnected) {
                clearInterval(this.timer);
                return;
                }
                await this.getLiveVersion();
            }, this.interval);
        },
        async getLiveVersion() {
            try {
                const response = await this.$apiInstance.get(`/webversions/live/${this.role}`);
                if((response.status !== 200 && response.status !== 201)) throw new Error();
                if(!response.data || !response.data.version) throw new Error();
                
                // 서비스 처음 접속한 경우
                if(localStorage.getItem('currentVersionHos') !== response.data.version) {
                    localStorage.setItem('currentVersionHos', response.data.version);
                    window.location.reload(true);
                }
            } catch (error) {
                console.log(error);
            }
        },
        setOnlineObserver() {
            window.addEventListener('online', ()=>{ this.checkIsOnline(true);});
            window.addEventListener('offline', ()=>{this.checkIsOnline(false);});
        },
        handleScreenSizeRedirection() {
            // 모바일 화면 대응은 WebChat SDK 내부에서 처리
        },
        toggleSidenav() {
            const navbar = document.querySelector('.navbar-vertical.navbar-expand-xs.fixed-start');
            navbar.style.left = this.navWidth;
        },
        async checkIsOnline(isOnline) {
            if(isOnline) {
                this.isInternetConnected = true;
            } else {
                this.isInternetConnected = false;
                await oneBtnDialog('인터넷 연결 끊김', '인터넷 연결 상태를 확인해 주세요.', false);
            }
        },
        getRoute() {
            const routeArr = this.$route.path.split('/');
            return routeArr[1];
        },
    },
};
</script>
<style>
.finger-pointer{
    position: fixed;
    bottom: 50px;
    right: 70px;
    z-index: 1000;
    opacity: 0;
    pointer-events: none
}
</style>